import { hash } from 'ohash'

interface KeyOptions {
  includePath?: boolean
  includeRouteParams?: boolean
  includeQuery?: boolean
  params?: Record<string, any>
  maxLength?: number
  useHashing?: boolean
}

export const useDataKey = () => {
  const route = useRoute()

  const normalizeValue = (value: any): string => {
    if (value === null || value === undefined) return 'null'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value).replace(/[^a-zA-Z0-9._-]/g, '-')
  }

  const hashLongString = (str: string, maxLength = 32): string => {
    if (str.length <= maxLength) return str
    return hash([str])
  }

  const generate = (
    entity: string,
    action?: string,
    options: KeyOptions = {},
  ): string => {
    const {
      includePath = true,
      includeRouteParams = true,
      includeQuery = false,
      params = {},
      maxLength = 100,
      useHashing = true,
    } = options

    const parts: string[] = []

    parts.push(entity)
    if (action) parts.push(action)

    if (includeRouteParams && route.params) {
      Object.entries(route.params).forEach(([key, value]) => {
        if (value) {
          parts.push(`${key}-${normalizeValue(value)}`)
        }
      })
    }

    if (includePath) {
      const normalizedPath = route.path
        .replace(/^\//, '')
        .replace(/\/$/, '')
        .replace(/[^a-zA-Z0-9]/g, '-')
        .replace(/-+/g, '-')

      if (normalizedPath) {
        parts.push(normalizedPath)
      }
    }

    if (includeQuery && Object.keys(route.query).length > 0) {
      const queryString = new URLSearchParams(
        Object.entries(route.query)
          .filter(([, value]) => value !== null && value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      ).toString()

      if (queryString) {
        parts.push(useHashing ? hashLongString(queryString) : normalizeValue(queryString))
      }
    }

    if (Object.keys(params).length > 0) {
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          const value = params[key]
          if (value !== null && value !== undefined) {
            acc[key] = value
          }
          return acc
        }, {} as Record<string, any>)

      if (Object.keys(sortedParams).length > 0) {
        const paramString = JSON.stringify(sortedParams)
        parts.push(useHashing ? hashLongString(paramString) : normalizeValue(paramString))
      }
    }

    let key = parts.filter(Boolean).join(':')

    if (useHashing && key.length > maxLength) {
      const hash = hashLongString(key, 16)
      const prefix = parts.slice(0, 2).join(':')
      key = `${prefix}:${hash}`
    }

    if (import.meta.dev && key.length > 150) {
      console.warn(`Long data key generated: ${key.substring(0, 100)}...`)
    }

    return key
  }

  return {
    generate,
    page: (action?: string, options?: KeyOptions) => generate('page', action, options),
    article: (action?: string, options?: KeyOptions) => generate('article', action, {
      includeRouteParams: true,
      ...options,
    }),
    category: (action?: string, options?: KeyOptions) => generate('category', action, {
      includeRouteParams: true,
      ...options,
    }),
    author: (action?: string, options?: KeyOptions) => generate('author', action, {
      includeRouteParams: true,
      includeQuery: true,
      ...options,
    }),
    breadcrumbs: (options?: KeyOptions) => generate('breadcrumbs', undefined, {
      includePath: true,
      includeRouteParams: true,
      ...options,
    }),
  }
}
